import type { BillingCycle } from "@/api/billing";

// Precisa ficar em sincronia com PREMIUM_PRICING no backend (billing.service.ts) - o preço
// mostrado e o preço cobrado de verdade vêm de constantes separadas. Em centavos pra os valores
// derivados abaixo não sofrerem arredondamento de ponto flutuante (99,90 / 12 = 8,325 → 8,33).
const MONTHLY_CENTS = 1990;
const YEARLY_CENTS = 9990;

const brl = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const yearlySavingsCents = MONTHLY_CENTS * 12 - YEARLY_CENTS;

/** Textos de preço já formatados (sem o "R$"), todos derivados dos dois valores acima. */
export const PRICING = {
  monthly: brl(MONTHLY_CENTS),
  yearly: brl(YEARLY_CENTS),
  yearlyPerMonth: brl(Math.round(YEARLY_CENTS / 12)),
  yearlySavings: brl(yearlySavingsCents),
  yearlyDiscountPercent: Math.round((yearlySavingsCents / (MONTHLY_CENTS * 12)) * 100),
  // Arredondado pra cima na dezena de centavos, pra frase "menos de R$ 0,30 por dia".
  yearlyPerDayUnder: brl(Math.ceil(YEARLY_CENTS / 365 / 10) * 10),
};

// Desconto na primeira cobrança que o admin dá pra contas específicas. Em sincronia com
// FIRST_CHARGE_DISCOUNT_PERCENT no backend (billing.service.ts), que é quem cobra de verdade.
const MONTHLY_DISCOUNT_PERCENT = 20;
const YEARLY_DISCOUNT_PERCENT = 40;
const monthlyFirstCents = Math.round((MONTHLY_CENTS * (100 - MONTHLY_DISCOUNT_PERCENT)) / 100);
const yearlyFirstCents = Math.round((YEARLY_CENTS * (100 - YEARLY_DISCOUNT_PERCENT)) / 100);

/** Textos do desconto na primeira cobrança, já formatados (sem o "R$"). */
export const DISCOUNT = {
  monthlyPercent: MONTHLY_DISCOUNT_PERCENT,
  yearlyPercent: YEARLY_DISCOUNT_PERCENT,
  monthlyFirst: brl(monthlyFirstCents),
  yearlyFirst: brl(yearlyFirstCents),
  yearlyFirstPerMonth: brl(Math.round(yearlyFirstCents / 12)),
  yearlyFirstSavings: brl(YEARLY_CENTS - yearlyFirstCents),
};

/** Valor numérico da primeira cobrança do ciclo, com ou sem o desconto (pro Meta Pixel). */
export function planValue(cycle: BillingCycle, discounted = false) {
  if (discounted) return (cycle === "YEARLY" ? yearlyFirstCents : monthlyFirstCents) / 100;
  return (cycle === "YEARLY" ? YEARLY_CENTS : MONTHLY_CENTS) / 100;
}

/** Até quando vale o desconto da conta, ou null se ela não tem (ou já venceu). */
export function activeDiscountUntil(discountUntil: string | null | undefined): Date | null {
  if (!discountUntil) return null;
  const until = new Date(discountUntil);
  return until.getTime() > Date.now() ? until : null;
}

/** "30/10" - o desconto vale até o fim desse dia no horário de Brasília. */
export function formatDiscountDeadline(until: Date) {
  return until.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", timeZone: "America/Sao_Paulo" });
}
